/**
 * Direct submission to Zendesk's anonymous end-user API, used for the "simple" contact ticket forms
 * (the ones without required custom/conditional fields — see SUBJECT_OPTIONS `handoff`). Anonymous
 * submission is enabled and the API is CORS-reachable from our origin, so no auth is needed. Forms with
 * required custom fields are handed off to the hosted form instead (handled in ContactForm).
 */

import { ZENDESK_REQUESTS_API, ZENDESK_UPLOADS_API } from './content'

export type ZendeskRequestInput = {
  ticketFormId: string
  /** Ticket subject line (the form's "Objet" field). */
  subject: string
  description: string
  requesterEmail: string
  file?: File | null
}

/** Upload a file to Zendesk and return its upload token (referenced from the request's comment). */
async function uploadAttachment(file: File): Promise<string> {
  const url = `${ZENDESK_UPLOADS_API}?filename=${encodeURIComponent(file.name)}`
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': file.type !== '' ? file.type : 'application/binary' },
    body: file,
  })
  if (!response.ok) {
    throw new Error(`Zendesk upload failed (${response.status})`)
  }
  const payload = (await response.json()) as { upload: { token: string } }
  return payload.upload.token
}

/** Create a Zendesk request (ticket). Uploads the attachment first when present. Throws on failure. */
export async function submitZendeskRequest(input: ZendeskRequestInput): Promise<void> {
  const uploads: string[] = []
  if (input.file != null && input.file.size > 0) {
    uploads.push(await uploadAttachment(input.file))
  }

  const response = await fetch(ZENDESK_REQUESTS_API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      request: {
        ticket_form_id: Number(input.ticketFormId),
        subject: input.subject,
        comment: { body: input.description, uploads },
        requester: { email: input.requesterEmail },
      },
    }),
  })

  if (!response.ok) {
    throw new Error(`Zendesk request failed (${response.status})`)
  }
}
